import { AxiosHeaders } from 'axios'
import type { InternalAxiosRequestConfig, AxiosError } from 'axios'
import { createHttpClient } from '@/lib/api/httpClient'
import { dispatchAuthUnauthorized } from '@/lib/auth/authEvents'
import { generateRequestId, setRequestId, clearRequestId, getRequestId } from '@/utils/requestId'
import logger from '@/utils/logger'

const rawBaseURL = (import.meta.env.VITE_API_BASE_URL ?? '').trim()

if (!rawBaseURL) {
  throw new Error('[API Client] VITE_API_BASE_URL が設定されていません。')
}

/**
 * HTTPS ページでの Mixed Content を避けるため、必要に応じて https に昇格させる
 */
const normalizeBaseURL = (input: string): string => {
  if (input.startsWith('/')) {
    if (typeof window === 'undefined') {
      throw new Error('[API Client] baseURL が相対パスのため、ブラウザ環境が必要です。')
    }
    return new URL(input, window.location.origin).toString()
  }

  if (!input.startsWith('http://')) {
    return input
  }

  if (typeof window === 'undefined' || window.location.protocol !== 'https:') {
    return input
  }

  try {
    const url = new URL(input)
    const hostname = url.hostname
    const isLocalHost = hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0' || hostname === '[::1]' || hostname.endsWith('.localhost')

    if (isLocalHost) {
      return input
    }

    url.protocol = 'https:'
    return url.toString()
  } catch (error) {
    throw new Error(`[API Client] baseURL の正規化に失敗しました: ${String(error)}`)
  }
}

/**
 * ベースURLを解決する。暗黙フォールバックは互換アダプタ内に閉じ込める。
 *
 * @returns 正規化済みベースURL
 */
const baseURL = normalizeBaseURL(rawBaseURL)

/**
 * 401 を auth context に通知する。
 */
const handleUnauthorized = (): void => {
  dispatchAuthUnauthorized()
}

const apiClient = createHttpClient({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
  withCredentials: true,
  onUnauthorized: handleUnauthorized,
})

type EnvelopePayload<T = unknown> = {
  success: boolean
  data?: T
  error?: string | { message?: string; code?: string; field?: string }
  meta?: {
    total?: number
    page?: number
    limit?: number
  }
}

export class ApiClientError extends Error {
  code?: string
  field?: string
  status?: number

  constructor(message: string, options?: { code?: string; field?: string; status?: number }) {
    super(message)
    this.name = 'ApiClientError'
    this.code = options?.code
    this.field = options?.field
    this.status = options?.status
  }
}

const isEnvelopePayload = (payload: unknown): payload is EnvelopePayload => {
  return typeof payload === 'object' && payload !== null && 'success' in payload
}

const resolveEnvelopeErrorMessage = (payload: EnvelopePayload): string => {
  if (typeof payload.error === 'string' && payload.error.length > 0) {
    return payload.error
  }
  if (
    payload.error &&
    typeof payload.error === 'object' &&
    typeof payload.error.message === 'string' &&
    payload.error.message.length > 0
  ) {
    return payload.error.message
  }
  return 'API request failed'
}

const resolveEnvelopeError = (
  payload: EnvelopePayload,
  status?: number,
): ApiClientError => {
  if (typeof payload.error === 'string' && payload.error.length > 0) {
    return new ApiClientError(payload.error, { status })
  }

  if (payload.error && typeof payload.error === 'object') {
    const message =
      typeof payload.error.message === 'string' && payload.error.message.length > 0
        ? payload.error.message
        : 'API request failed'
    return new ApiClientError(message, {
      code: typeof payload.error.code === 'string' ? payload.error.code : undefined,
      field: typeof payload.error.field === 'string' ? payload.error.field : undefined,
      status,
    })
  }

  return new ApiClientError('API request failed', { status })
}

const resolveHttpErrorMessage = (payload: unknown): string | null => {
  if (isEnvelopePayload(payload)) {
    const message = resolveEnvelopeErrorMessage(payload)
    return message.length > 0 ? message : null
  }

  if (payload && typeof payload === 'object') {
    if ('detail' in payload && typeof payload.detail === 'string' && payload.detail.length > 0) {
      return payload.detail
    }
    if ('error' in payload) {
      const errorValue = payload.error
      if (typeof errorValue === 'string' && errorValue.length > 0) {
        return errorValue
      }
      if (
        errorValue &&
        typeof errorValue === 'object' &&
        'message' in errorValue &&
        typeof errorValue.message === 'string' &&
        errorValue.message.length > 0
      ) {
        return errorValue.message
      }
    }
  }

  return null
}

const resolveHttpError = (payload: unknown, status?: number): ApiClientError | null => {
  if (isEnvelopePayload(payload)) {
    return resolveEnvelopeError(payload, status)
  }

  if (payload && typeof payload === 'object') {
    if ('detail' in payload && typeof payload.detail === 'string' && payload.detail.length > 0) {
      return new ApiClientError(payload.detail, { status })
    }
    if ('error' in payload) {
      const errorValue = payload.error
      if (typeof errorValue === 'string' && errorValue.length > 0) {
        return new ApiClientError(errorValue, { status })
      }
      if (errorValue && typeof errorValue === 'object') {
        const message =
          'message' in errorValue && typeof errorValue.message === 'string' && errorValue.message.length > 0
            ? errorValue.message
            : null
        if (message) {
          return new ApiClientError(message, {
            code: 'code' in errorValue && typeof errorValue.code === 'string' ? errorValue.code : undefined,
            field: 'field' in errorValue && typeof errorValue.field === 'string' ? errorValue.field : undefined,
            status,
          })
        }
      }
    }
  }

  return null
}

apiClient.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    // リクエストIDを設定。呼び出し側が指定していればそれを使う
    // （認証確認は自分が採番した ID で経過を記録するため。PRI-251）
    const headers = config.headers instanceof AxiosHeaders
      ? config.headers
      : new AxiosHeaders(config.headers ?? {})

    const presetRequestId = headers.get('X-Request-ID')
    const requestId =
      typeof presetRequestId === 'string' && presetRequestId.length > 0
        ? presetRequestId
        : generateRequestId()
    setRequestId(requestId)

    headers.set('X-Request-ID', requestId)
    config.headers = headers

    if (config.url && config.url.startsWith('/') && !config.url.startsWith('//')) {
      config.url = config.url.slice(1)
    }

    // リクエストログ（開発環境のみ）
    logger.debug('API request', {
      method: config.method?.toUpperCase(),
      url: config.url,
      requestId,
    })

    return config
  },
  (error: AxiosError) => {
    logger.error('API request failed', error, {
      requestId: getRequestId(),
    })
    clearRequestId()
    return Promise.reject(error)
  }
)

apiClient.interceptors.response.use(
  (response) => {
    const requestId = getRequestId()
    
    // レスポンスログ（開発環境のみ）
    logger.debug('API response', {
      method: response.config.method?.toUpperCase(),
      url: response.config.url,
      status: response.status,
      requestId,
    })

    if (isEnvelopePayload(response.data)) {
      if (!response.data.success) {
        const envelopeError = resolveEnvelopeError(response.data, response.status)
        logger.error('API envelope error', envelopeError, {
          ...response.data.meta,
          method: response.config.method?.toUpperCase(),
          url: response.config.url,
          status: response.status,
          requestId,
        })
        clearRequestId()
        return Promise.reject(envelopeError)
      }
      response.data = response.data.data
    }

    clearRequestId()
    return response
  },
  (error: AxiosError) => {
    const requestId = getRequestId()
    const responseData = error.response?.data
    const meta = {
      method: error.config?.method?.toUpperCase(),
      url: error.config?.url,
      status: error.response?.status,
      statusText: error.response?.statusText,
      requestId,
    }

    // タイムアウトやネットワーク切断は一時的なため WARN
    if (error.code === 'ECONNABORTED' || !error.response) {
      logger.warn('API request timeout or network error', meta)
    } else if (error.response.status === 401) {
      // 未ログイン / セッション失効による 401 は「状態」であって不具合ではない。
      // ロガーに ERROR で吐くとブラウザコンソールが赤く埋まり開発体験を阻害するため INFO に。
      // auth context 側が status===401 を検出して login 画面遷移などを担当する。
      logger.info('Unauthenticated request (401)', { ...meta })
      const structuredError = resolveHttpError(responseData, error.response.status)
      if (structuredError) {
        clearRequestId()
        return Promise.reject(structuredError)
      }
    } else {
      logger.error('API error', error, { ...meta, responseData })
      const structuredError = resolveHttpError(responseData, error.response?.status)
      if (structuredError) {
        clearRequestId()
        return Promise.reject(structuredError)
      }
    }

    clearRequestId()
    return Promise.reject(error)
  }
)

export default apiClient
